import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
    }

    if (loadsNewProduct) url.pathname = newUrl;

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) radios[currentIndex].dataset.previousChecked = 'false';
          if (previousIndex !== undefined && radios[previousIndex]) radios[previousIndex].dataset.previousChecked = 'false';

          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) option.removeAttribute('selected');
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      else params.push(`option_values=${selectedOption.dataset.optionValueId}`);
    }

    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') console.warn('Fetch aborted by user');
        else console.error(error);
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined}
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newVariantPickerSource) throw new Error('No new variant picker source found');

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');
    if (!main || !newMain) throw new Error('No new main source found');
    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined}
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) return undefined;
    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined}
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) throw new Error('No option value ID found');
    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]}
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

/* ---------------------------------------------------------
   CUSTOM MEASURE LOGIC (Höhe/Breite)
   - scoped: nur dort, wo measure-inputs + variant-picker zusammen sind
   - verhindert Side-Effects im Warenkorb/Drawer
--------------------------------------------------------- */
(function () {
  const STORAGE_KEY = 'measure_inputs_v1';

  function loadStored() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  }

  function saveStored(data) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  function parseNumber(val) {
    if (val == null) return NaN;
    const s = String(val).trim().replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : NaN;
  }

  function findContextFor(container) {
    // measure-inputs steht bei dir direkt vor <variant-picker>
    const parent = container.parentElement || document;
    const vp = parent.querySelector('variant-picker');
    if (!vp) return null;

    // nicht in Warenkorb/Drawer/Quick add binden
    if (
      container.closest('cart-drawer') ||
      container.closest('cart-notification') ||
      container.closest('quick-add-dialog') ||
      container.closest('product-card')
    ) {
      return null;
    }

    return { container, vp };
  }

  function getOptionContainer(vp, optionName) {
    return vp.querySelector('.variant-option[data-option-name="' + optionName + '"]');
  }

  function getOptionValues(vp, optionName) {
    const container = getOptionContainer(vp, optionName);
    if (!container) return [];

    const radios = container.querySelectorAll('input[type="radio"]');
    if (radios && radios.length) {
      return Array.from(radios)
        .map((r) => ({ raw: r.value, num: parseNumber(r.value), el: r }))
        .filter((x) => Number.isFinite(x.num))
        .sort((a, b) => a.num - b.num);
    }

    const select = container.querySelector('select');
    if (select) {
      return Array.from(select.options)
        .map((o) => ({ raw: o.value, num: parseNumber(o.value), el: select }))
        .filter((x) => Number.isFinite(x.num))
        .sort((a, b) => a.num - b.num);
    }

    return [];
  }

  // NEU: immer auf den nächsten vorhandenen Wert AUFRUNDEN (Ceil auf Variantenwerte)
  function selectByVariantCeil(vp, optionName, inputNumber) {
    if (!Number.isFinite(inputNumber)) return;

    const values = getOptionValues(vp, optionName);
    if (!values.length) return;

    // kleinster Wert >= input, sonst größter Wert
    let chosen = values[values.length - 1];
    for (let i = 0; i < values.length; i++) {
      if (values[i].num >= inputNumber) {
        chosen = values[i];
        break;
      }
    }

    if (chosen.el.tagName === 'INPUT') {
      chosen.el.click();
    } else {
      chosen.el.value = chosen.raw;
      chosen.el.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function syncLineItemProperties(scopeEl, heightValue, widthValue) {
    // möglichst nah am Variant Picker bleiben
    const root = scopeEl.closest('section') || document;
    const productForm = root.querySelector('form[action^="/cart/add"]');
    if (!productForm) return;

    function upsertHidden(name, value) {
      let el = productForm.querySelector('input[name="' + name.replace(/"/g, '\\"') + '"]');
      if (!el) {
        el = document.createElement('input');
        el.type = 'hidden';
        el.name = name;
        productForm.appendChild(el);
      }
      el.value = value;
    }

    upsertHidden('properties[Höhe (eingegeben)]', heightValue);
    upsertHidden('properties[Breite (eingegeben)]', widthValue);
  }

  function bindMeasureBlock(measureBlock) {
    const ctx = findContextFor(measureBlock);
    if (!ctx) return;

    const { vp } = ctx;

    const heightInput = measureBlock.querySelector('[id^="custom-height-"]');
    const widthInput = measureBlock.querySelector('[id^="custom-width-"]');
    const applyBtn = measureBlock.querySelector('[id^="apply-measures-"]');

    if (!heightInput || !widthInput || !applyBtn) return;

    // ✅ Platzhalter setzen
    heightInput.setAttribute('placeholder', 'Ihre Höhe');
    widthInput.setAttribute('placeholder', 'Ihre Breite');

    if (measureBlock.dataset.bound === '1') return;
    measureBlock.dataset.bound = '1';

    const stored = loadStored();
    if (stored.height != null && stored.height !== '') heightInput.value = stored.height;
    if (stored.width != null && stored.width !== '') widthInput.value = stored.width;

    syncLineItemProperties(measureBlock, heightInput.value, widthInput.value);

    function applyNow() {
      saveStored({ height: heightInput.value, width: widthInput.value });
      syncLineItemProperties(measureBlock, heightInput.value, widthInput.value);

      // 1) erst Höhe runden + auswählen
      selectByVariantCeil(vp, 'Höhe', parseNumber(heightInput.value));

      // 2) danach (nach Morph/Fetch) Breite runden + auswählen
      requestYieldCallback(() => {
        const freshVp = (ctx.container.parentElement || document).querySelector('variant-picker') || vp;
        selectByVariantCeil(freshVp, 'Breite', parseNumber(widthInput.value));
      });
    }

    applyBtn.addEventListener('click', applyNow);

    [heightInput, widthInput].forEach((el) => {
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          applyNow();
        }
      });
    });
  }

  function initAll() {
    document.querySelectorAll('.measure-inputs').forEach(bindMeasureBlock);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);

  // nur leicht beobachten, aber nicht alles “blind” umbinden
  const obs = new MutationObserver(() => initAll());
  obs.observe(document.documentElement, { childList: true, subtree: true });
})();